import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { catchError, filter, first, map, tap } from 'rxjs/operators';

import { State } from '®app/ngrx';
import { BibliographicResource } from '®shared/bibliographic-resource';
import { Loadable } from '®shared/data';
import { handleRenavigationError, idWithoutDotHtml } from '®shared/router';

import { FetchBibliographicResourceAction } from './resource.actions';
import { selectResourceWithId } from './resource.state';

export type LoadableResource = Loadable<BibliographicResource>;

@Injectable()
export class ResourceGuard
  implements CanActivate, Resolve<BibliographicResource>
{
  constructor(private router: Router, private store: Store<State>) {}

  resolve(
    snapshot: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<BibliographicResource> {
    return this.fetchResourceForRoute(snapshot);
  }

  canActivate(snapshot: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this.fetchResourceForRoute(snapshot).pipe(
      map(bibliographicResource => (bibliographicResource ? true : false)),
      catchError(handleRenavigationError(this.router)),
    );
  }
  private fetchResourceForRoute(
    snapshot: ActivatedRouteSnapshot,
  ): Observable<BibliographicResource> {
    const resourceId = idWithoutDotHtml(snapshot.params.resourceId);

    return this.store.select(selectResourceWithId(resourceId)).pipe(
      tap((loadableResource: LoadableResource | null) => {
        if (!loadableResource) {
          this.store.dispatch(
            new FetchBibliographicResourceAction({ resourceId }),
          );
        }
      }),
      filter((loadableResource: LoadableResource | null) =>
        loadableResource?.data || loadableResource?.loadingError ? true : false,
      ),
      map((loadableResource: LoadableResource) => {
        if (loadableResource.loadingError) {
          throw loadableResource.loadingError;
        }
        return loadableResource.data;
      }),
      first(),
    );
  }
}
